import { ComputedRef, computed, ref } from 'vue';
import { LeafNode, ParentNode } from '@/ts/composables/useMarketCarpetData';

const SIZE_BY_KEYS = ['market-cap', 'market-cap-adjusted', 'equal-weight', 'price'] as const;
type SizeByKey = typeof SIZE_BY_KEYS[number];

const DEFAULT_SIZE_BY: SizeByKey = 'market-cap-adjusted';

function isValidSizeBy(value: string): value is SizeByKey {
  return (SIZE_BY_KEYS as readonly string[]).includes(value);
}

// Factory for creating instance-scoped size-by state
export function createSizeBy(
  dependencies: { leafNodes: ComputedRef<LeafNode[]> },
  options?: { sizeBy?: SizeByKey }
) {
  const sizeByKey = ref<SizeByKey>(options?.sizeBy ?? DEFAULT_SIZE_BY);

  const nodeCount = computed(() => dependencies.leafNodes.value.length);
  const totalMarketCap = computed(() => {
    return dependencies.leafNodes.value.reduce((acc, node) => acc + (node.marketCap ?? 0), 0);
  });

  // Smallest positive market cap across all leaf nodes. Used as the floor
  // for symbols with null/zero market cap so they still get a visible box.
  // Matches the legacy freecharts carpet.js behavior where per-sector
  // normalization to [1, 100] gave null-cap symbols the same size (1) as
  // the smallest real cap in the dataset.
  const minPositiveMarketCap = computed(() => {
    let min = Infinity;
    for (const node of dependencies.leafNodes.value) {
      if (node.marketCap !== null && node.marketCap > 0 && node.marketCap < min)
        min = node.marketCap;
    }
    return min === Infinity ? 1 : min;
  });

  // Intentionally inside the factory -- 'market-cap-adjusted' closes over
  // the instance-scoped totalMarketCap/nodeCount computeds above.
  const SIZE_BY_MAP = {
    'market-cap': (obj: LeafNode | ParentNode) => {
      if (!('marketCap' in obj))
        return 0;
      const marketCap = obj.marketCap ?? 0;
      if (marketCap > 0)
        return marketCap;
      return minPositiveMarketCap.value;
    },
    'market-cap-adjusted': (obj: LeafNode | ParentNode) => {
      const average = totalMarketCap.value / nodeCount.value;
      if (!('marketCap' in obj))
        return 0;
      const marketCap = obj.marketCap ?? 0;
      const INFLATION_FACTOR = 2;
      const MIN_MARKET_CAP = average > 0 ? average / INFLATION_FACTOR : minPositiveMarketCap.value;
      return Math.max(marketCap, MIN_MARKET_CAP);
    },
    'equal-weight': (obj: LeafNode | ParentNode) => 'children' in obj ? 0 : 1,
    'price': (obj: LeafNode | ParentNode) => 'lastPrice' in obj ? obj.lastPrice : 0,
  };

  const sizeBy = computed(() => {
    return SIZE_BY_MAP[sizeByKey.value];
  });

  return { sizeByKey, sizeBy };
}

export type { SizeByKey };

export {
  DEFAULT_SIZE_BY,
  isValidSizeBy
};
